<template>
  <router-view v-if="$route.meta && $route.meta.layout === 'empty'" />
  <el-container v-else class="app-wrapper">
    <!-- 侧边栏 -->
    <el-aside width="240px" class="sidebar-container">
      <div class="logo-container">
        <div class="logo-icon">
          <svg viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg" width="28" height="28">
            <circle cx="18" cy="18" r="16" stroke="#00d4ff" stroke-width="2"/>
            <path d="M10 22 L18 12 L26 22" stroke="#00d4ff" stroke-width="2.5" stroke-linecap="round"/>
            <circle cx="18" cy="24" r="3" fill="#00d4ff"/>
          </svg>
        </div>
        <div class="logo-text">
          <div class="title">{{ tenantConfig.brandTitle }}</div>
          <div class="subtitle">{{ tenantConfig.brandSubtitle }}</div>
        </div>
      </div>

      <el-menu
        :default-active="$route.path"
        class="sidebar-menu"
        @select="handleMenuSelect"
      >
        <template v-for="section in filteredMenuSections" :key="section.label">
          <div class="menu-section-label">{{ section.label }}</div>
          <el-menu-item
            v-for="item in section.items"
            :key="item.path"
            :index="item.path"
            :class="item.className || ''"
          >
            <el-icon>
              <component :is="item.icon" />
            </el-icon>
            <template #title>
              <span style="flex:1">{{ item.title }}</span>
              <span v-if="item.badge" :class="item.badge.className">{{ item.badge.text }}</span>
            </template>
          </el-menu-item>
        </template>
      </el-menu>


      <!-- 主题切换 -->
      <div class="theme-toggle" @click="toggleTheme">
        <div class="theme-toggle-track" :class="{ light: !isDark }">
          <div class="theme-toggle-thumb">
            <span class="theme-icon">{{ isDark ? '深' : '浅' }}</span>
          </div>
        </div>
        <span class="theme-label">{{ isDark ? '深色' : '浅色' }}</span>
      </div>

      <!-- 底部状态 -->
      <div class="sidebar-footer">
        <div class="status-dot"></div>
        <span>服务正常</span>
      </div>
    </el-aside>

    <!-- 主内容区 -->
    <el-container class="main-container">
      <el-header class="main-header">
        <div class="header-left">
          <div class="page-title">{{ getCurrentMenuTitle() }}</div>
          <el-breadcrumb separator="/" class="breadcrumb">
            <el-breadcrumb-item :to="{ path: '/' }">首页</el-breadcrumb-item>
            <el-breadcrumb-item>{{ getCurrentMenuTitle() }}</el-breadcrumb-item>
          </el-breadcrumb>
        </div>
        <div class="header-right">
          <div class="header-time">{{ currentTime }}</div>
          <div class="header-divider"></div>
          <el-dropdown @command="onDropdownCommand" trigger="click">
            <div class="user-info">
              <div class="user-avatar">{{ userAvatarText }}</div>
              <span class="user-name">{{ userDisplayName }}</span>
              <el-icon class="caret"><CaretBottom /></el-icon>
            </div>
            <template #dropdown>
              <el-dropdown-menu>
                <el-dropdown-item command="settings">账号设置</el-dropdown-item>
                <el-dropdown-item command="logout" divided>退出登录</el-dropdown-item>
              </el-dropdown-menu>
            </template>
          </el-dropdown>
        </div>
      </el-header>

      <el-main class="app-main">
        <div v-if="routeSwitching" class="route-switching-tip">正在打开页面...</div>
        <router-view v-slot="{ Component }">
          <component :is="Component" />
        </router-view>
      </el-main>
    </el-container>
  </el-container>
</template>

<script>
import { computed, nextTick, ref, onMounted, onUnmounted, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { applyTenantConfig, tenantConfig } from '@/config/tenant'
import { clearAuth, getCurrentUser, getCurrentUserRole, getToken } from '@/utils/auth'
import { getBreadcrumbMap, menuSections } from '@/config/menu'
import request from '@/utils/request'
import {
  DataLine, Tickets, Box, House, Van, User, Setting,
  CaretBottom, RefreshLeft, Position, Download, OfficeBuilding, ChatDotRound, Refresh
} from '@element-plus/icons-vue'

export default {
  name: 'App',
  components: { DataLine, Tickets, Box, House, Van, User, Setting, CaretBottom, RefreshLeft, Position, Download, OfficeBuilding, ChatDotRound, Refresh },
  setup() {
    const route = useRoute()
    const router = useRouter()
    const currentMenuTitle = ref('数据概览')
    const currentTime = ref('')
    const currentUser = ref(getCurrentUser() || {})
    const currentRole = computed(() => getCurrentUserRole())
    const isDark = ref(true)
    const routeSwitching = ref(false)

    const toggleTheme = () => {
      isDark.value = !isDark.value
      const theme = isDark.value ? 'dark' : 'light'
      document.documentElement.dataset.theme = theme
      localStorage.setItem('mpplus-theme', theme)
    }

    const breadcrumbMap = getBreadcrumbMap()

    const hasAuthPermission = (requiredAuth) => {
      if (!requiredAuth) return true
      const user = currentUser.value || {}
      const perms = Array.isArray(user?.perms) ? user.perms.map(p => String(p || '').trim()) : []
      if (perms.includes('*')) return true

      const matchOne = (required) => {
        const req = String(required || '').trim()
        if (!req) return true
        if (perms.includes(req)) return true
        // 支持 api:GET:/api/orders/* 前缀匹配
        return perms.some((p) => p.endsWith('/*') && req.startsWith(p.slice(0, -1)))
      }

      if (Array.isArray(requiredAuth)) {
        return requiredAuth.some(matchOne)
      }
      return matchOne(requiredAuth)
    }

    const filteredMenuSections = computed(() => {
      const role = String(currentRole.value || '').toLowerCase()
      const roleAllowed = (roles) => {
        if (!Array.isArray(roles) || roles.length === 0) return true
        return roles.map(r => String(r).toLowerCase()).includes(role)
      }
      return menuSections
        .map(section => ({
          ...section,
          items: section.items.filter(item => roleAllowed(item.roles) && hasAuthPermission(item.auth))
        }))
        .filter(section => section.items.length > 0)
    })

    const updateTime = () => {
      const now = new Date()
      currentTime.value = now.toLocaleString('zh-CN', { hour12: false })
    }

    let timer = null
    let receiptConfigToken = ''

    const syncReceiptConfig = async () => {
      const token = getToken()
      if (!token || receiptConfigToken === token) return
      try {
        const settings = await request.get('/api/v1/system/receipt-settings', { silent: true })
        applyTenantConfig(settings)
        receiptConfigToken = token
      } catch (_) {
        // 本地缓存继续作为断网或旧后端的兜底配置。
      }
    }

    onMounted(() => {
      updateTime()
      timer = setInterval(updateTime, 30000)
      // 读取用户主题偏好
      const savedTheme = localStorage.getItem('mpplus-theme') || 'dark'
      isDark.value = savedTheme === 'dark'
      document.documentElement.dataset.theme = savedTheme
      syncReceiptConfig()
    })
    onUnmounted(() => {
      clearInterval(timer)
    })

    const updateMenuTitle = () => {
      const matched = Object.keys(breadcrumbMap)
        .filter(p => route.path === p || (route.path.startsWith(p) && p !== '/'))
        .sort((a, b) => b.length - a.length)[0]
      currentMenuTitle.value = matched ? breadcrumbMap[matched] : '今日工作台'
    }

    onMounted(updateMenuTitle)
    watch(() => route.path, updateMenuTitle)
    watch(() => route.fullPath, () => {
      routeSwitching.value = false
      currentUser.value = getCurrentUser() || {}
      syncReceiptConfig()
    })
    const getCurrentMenuTitle = () => currentMenuTitle.value

    const userDisplayName = computed(() => {
      return currentUser.value?.nickname || currentUser.value?.username || '用户'
    })
    const userAvatarText = computed(() => {
      const role = String(currentUser.value?.role || '').toLowerCase()
      if (role === 'admin') return '管'
      return String(userDisplayName.value || '用').slice(0, 1)
    })

    const onDropdownCommand = (cmd) => {
      if (cmd === 'settings') {
        const target = '/settings/users'
        if (route.path !== target) {
          router.push(target).catch((err) => {
            if (!err || err.name === 'NavigationDuplicated') return
            console.warn('[account settings navigation]', err)
            window.location.href = target
          })
        }
        return
      }
      if (cmd === 'logout') handleLogout()
    }

    const handleMenuSelect = async (path) => {
      if (!path || path === route.path) return
      routeSwitching.value = true
      await nextTick()
      router.push(path).catch((err) => {
        routeSwitching.value = false
        if (!err || err.name === 'NavigationDuplicated') return
        console.warn('[menu navigation]', err)
        window.location.href = path
      })
    }

    const handleLogout = () => {
      clearAuth()
      router.replace('/login').catch(() => {
        window.location.href = '/login'
      })
    }

    return {
      currentMenuTitle,
      getCurrentMenuTitle,
      currentTime,
      routeSwitching,
      tenantConfig,
      userDisplayName,
      userAvatarText,
      handleLogout,
      onDropdownCommand,
      handleMenuSelect,
      filteredMenuSections,
      isDark,
      toggleTheme
    }
  }
}
</script>

<style lang="scss">
* { box-sizing: border-box; }

html, body, #app {
  margin: 0; padding: 0; height: 100%;
  min-width: 1180px;
  overflow: auto;
  font-family: 'Microsoft YaHei', 'PingFang SC', 'Noto Sans SC', 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
}

/* 全局深色滚动条：页面/弹窗/表格统一 */
::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}
::-webkit-scrollbar-thumb {
  background-color: #434b55;
  border-radius: 4px;
}
::-webkit-scrollbar-thumb:hover {
  background-color: #5a6370;
}
::-webkit-scrollbar-track {
  background: transparent;
}

// ===== Element Plus 组件级覆盖 =====
.el-dialog {
  background: var(--bg-elevated) !important;
  border: 1px solid var(--border-default);
  border-radius: var(--radius-lg) !important;
  .el-dialog__header { border-bottom: 1px solid var(--border-default); }
  .el-dialog__title { color: var(--text-primary) !important; }
}
.el-drawer {
  background: var(--bg-elevated) !important;
  .el-drawer__header { color: var(--text-primary) !important; border-bottom: 1px solid var(--border-default); }
}
.el-input__wrapper { background: var(--input-bg) !important; box-shadow: 0 0 0 1px var(--input-border) inset !important; }
.el-input__inner { color: var(--text-primary) !important; background: transparent !important; }
.el-input__wrapper:hover { box-shadow: 0 0 0 1px var(--color-primary) inset !important; }
.el-input__wrapper.is-focus { box-shadow: 0 0 0 1px var(--color-primary) inset !important; }
.el-select .el-input__wrapper { background: var(--input-bg) !important; }
.el-select-dropdown { background: var(--bg-elevated) !important; border-color: var(--border-default) !important; }
/* 修复：下拉选项 selected + hover 时出现“白底白字”导致看不清 */
.el-select-dropdown__item {
  color: var(--text-primary) !important;
  &.hover,
  &:hover {
    background: var(--bg-hover) !important;
    color: var(--text-primary) !important;
  }
  &.selected,
  &.is-selected {
    color: var(--color-primary) !important;
    background: var(--color-primary-bg) !important;
  }
  &.selected.hover,
  &.selected:hover,
  &.is-selected.hover,
  &.is-selected:hover {
    color: var(--color-primary) !important;
    background: var(--color-primary-bg) !important;
  }
}
.el-popper { background: var(--bg-elevated) !important; border-color: var(--border-default) !important; .el-popper__arrow::before { border-color: var(--border-default) !important; } }

/* 统一下拉框模板（推荐在业务页使用 UiDarkSelect） */
.ui-dark-select .el-input__wrapper {
  background: var(--input-bg) !important;
  box-shadow: 0 0 0 1px var(--input-border) inset !important;
}
.ui-dark-select .el-input__wrapper:hover,
.ui-dark-select .el-input__wrapper.is-focus {
  box-shadow: 0 0 0 1px var(--color-primary) inset !important;
}
.ui-dark-select .el-input__inner {
  color: var(--text-primary) !important;
}
.ui-dark-select-popper.el-popper,
.ui-dark-select-popper .el-select-dropdown {
  background: var(--bg-elevated) !important;
  border-color: var(--border-default) !important;
}
.ui-dark-select-popper .el-select-dropdown__item {
  color: var(--text-primary) !important;
}
.ui-dark-select-popper .el-select-dropdown__item.hover,
.ui-dark-select-popper .el-select-dropdown__item:hover {
  background: var(--bg-hover) !important;
  color: var(--text-primary) !important;
}
.ui-dark-select-popper .el-select-dropdown__item.selected,
.ui-dark-select-popper .el-select-dropdown__item.is-selected {
  color: var(--color-primary) !important;
  background: var(--color-primary-bg) !important;
}
.ui-dark-select-popper .el-select-dropdown__item.selected.hover,
.ui-dark-select-popper .el-select-dropdown__item.selected:hover,
.ui-dark-select-popper .el-select-dropdown__item.is-selected.hover,
.ui-dark-select-popper .el-select-dropdown__item.is-selected:hover {
  color: var(--color-primary) !important;
  background: var(--color-primary-bg) !important;
}
.ui-dark-select-popper .el-popper__arrow::before {
  border-color: var(--border-default) !important;
}
.el-date-picker { background: var(--bg-elevated) !important; border-color: var(--border-default) !important; .el-date-picker__header { color: var(--text-primary) !important; } }
.el-picker-panel { background: var(--bg-elevated) !important; border-color: var(--border-default) !important; color: var(--text-primary) !important; }
.el-date-range-picker { background: var(--bg-elevated) !important; border-color: var(--border-default) !important; }
.el-date-table td { color: var(--text-secondary) !important; &.in-range div, &.in-range div:hover { background: var(--color-primary-bg) !important; } }
.el-tag { background: var(--color-primary-bg) !important; border-color: var(--border-default) !important; color: var(--color-primary) !important; &.el-tag--success { background: var(--color-success-bg) !important; border-color: var(--border-default) !important; color: var(--color-success) !important; } &.el-tag--warning { background: var(--color-warning-bg) !important; border-color: var(--border-default) !important; color: var(--color-warning) !important; } &.el-tag--danger { background: var(--color-danger-bg) !important; border-color: var(--border-default) !important; color: var(--color-danger) !important; } }
.el-pagination { color: var(--text-secondary) !important; background: transparent !important; .el-pagination__total { color: var(--text-muted) !important; } .el-pager li { background: var(--bg-card) !important; color: var(--text-secondary) !important; border: 1px solid var(--border-color) !important; &.is-active { background: var(--accent-blue) !important; color: #000 !important; border-color: var(--accent-blue) !important; } } .btn-prev, .btn-next { background: var(--bg-card) !important; color: var(--text-secondary) !important; border: 1px solid var(--border-color) !important; } }
.el-loading-mask { background: var(--overlay-bg) !important; }
.el-message-box { background: var(--bg-elevated) !important; border: 1px solid var(--border-default) !important; .el-message-box__title { color: var(--text-primary) !important; } .el-message-box__content { color: var(--text-secondary) !important; } }
.el-form-item__label { color: var(--text-secondary) !important; }
.el-input-number .el-input__wrapper { background: var(--input-bg) !important; }
.el-select-v2__wrapper { background: var(--input-bg) !important; box-shadow: 0 0 0 1px var(--input-border) inset !important; color: var(--text-primary) !important; }
.el-textarea__inner { background: var(--input-bg) !important; color: var(--text-primary) !important; box-shadow: 0 0 0 1px var(--input-border) inset !important; &:focus { box-shadow: 0 0 0 1px var(--color-primary) inset !important; } }

// ======== 布局 ========
.app-wrapper {
  height: 100%;
  min-width: 1180px;
  background: var(--bg-page);
}

// ======== 侧边栏 ========
.sidebar-container {
  height: 100%;
  background: var(--bg-secondary) !important;
  border-right: 1px solid var(--border-color);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  position: relative;
  z-index: 100;

  .logo-container {
    height: var(--header-height);
    padding: 0 20px;
    display: flex;
    align-items: center;
    gap: 12px;
    border-bottom: 1px solid var(--border-color);
    flex-shrink: 0;

    .logo-icon {
      width: 36px; height: 36px;
      background: var(--accent-blue-dim);
      border-radius: 10px;
      display: flex; align-items: center; justify-content: center;
      flex-shrink: 0;
    }

    .logo-text {
      .title {
        font-size: 15px;
        font-weight: 700;
        color: var(--text-primary);
        letter-spacing: 0.5px;
      }
      .subtitle {
        font-size: 11px;
        color: var(--text-muted);
        margin-top: 1px;
      }
    }
  }

  .menu-section-label {
    padding: 16px 20px 6px;
    font-size: 11px;
    font-weight: 600;
    color: var(--text-muted);
    text-transform: uppercase;
    letter-spacing: 1px;
    flex-shrink: 0;
  }

  .sidebar-menu {
    flex: 1;
    background: transparent !important;
    border: none !important;
    overflow-y: auto;
    padding: 0 10px;
    pointer-events: auto;

    &::-webkit-scrollbar { width: 4px; }
    &::-webkit-scrollbar-track { background: transparent; }
    &::-webkit-scrollbar-thumb { background: var(--border-color); border-radius: 2px; }

    .el-menu-item {
      height: 42px;
      line-height: 42px;
      border-radius: var(--radius);
      margin: 2px 0;
      color: var(--text-secondary) !important;
      transition: all 0.2s;
      font-size: 14px;

      .el-icon { color: var(--text-muted) !important; }

      &:hover {
        background: var(--bg-hover) !important;
        color: var(--text-primary) !important;
        .el-icon { color: var(--text-primary) !important; }
      }

      &.is-active {
        background: var(--accent-blue-dim) !important;
        color: var(--accent-blue) !important;
        .el-icon { color: var(--accent-blue) !important; }
      }

      &.highlight-item.is-active {
        background: var(--bg-hover) !important;
        border: 1px solid var(--border-hover) !important;
      }

      .hot-tag {
        margin-left: 6px;
        background: var(--accent-blue-dim) !important;
        color: var(--accent-blue) !important;
        border: 1px solid rgba(0,212,255,0.3) !important;
        font-size: 10px;
        padding: 0 5px;
        height: 18px;
        line-height: 16px;
      }

      // 线上接单专属高亮
      &.menu-highlight {
        .el-menu-item__content {
          display: flex !important;
          align-items: center !important;
        }
        &.is-active {
          background: rgba(163,122,249,0.12) !important;
          color: var(--accent-purple) !important;
          .el-icon { color: var(--accent-purple) !important; }
        }
        &:hover:not(.is-active) {
          color: var(--accent-purple) !important;
          .el-icon { color: var(--accent-purple) !important; }
        }
      }

      .menu-new-badge {
        font-size: 9px;
        font-weight: 800;
        letter-spacing: 0.5px;
        background: linear-gradient(135deg, #a78bfa, #7c3aed);
        color: white;
        padding: 1px 5px;
        border-radius: 4px;
        margin-left: 4px;
      }
    }
  }

  // ======== 主题切换开关 ========
  .theme-toggle {
    padding: 8px 20px;
    border-top: 1px solid var(--border-color);
    display: flex;
    align-items: center;
    gap: 10px;
    cursor: pointer;
    flex-shrink: 0;
    transition: background 0.2s;

    &:hover {
      background: var(--bg-hover);
    }
  }

  .theme-toggle-track {
    width: 40px;
    height: 22px;
    border-radius: 11px;
    background: var(--neutral-700);
    position: relative;
    transition: background 0.3s;
    flex-shrink: 0;

    &.light {
      background: var(--brand-primary);
    }
  }

  .theme-toggle-thumb {
    width: 18px;
    height: 18px;
    border-radius: 50%;
    background: var(--bg-primary);
    position: absolute;
    top: 2px;
    left: 2px;
    transition: transform 0.3s;
    display: flex;
    align-items: center;
    justify-content: center;

    .light & {
      transform: translateX(18px);
    }
  }

  .theme-icon {
    font-size: 11px;
    line-height: 1;
  }

  .theme-label {
    font-size: 12px;
    color: var(--text-muted);
    user-select: none;
  }

  .sidebar-footer {
    padding: 12px 20px;
    border-top: 1px solid var(--border-color);
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 12px;
    color: var(--text-muted);
    flex-shrink: 0;

    .status-dot {
      width: 8px; height: 8px;
      border-radius: 50%;
      background: var(--accent-green);
      box-shadow: none;
    }
  }
}



// ======== 主内容区 ========
.main-container {
  height: 100%;
  min-width: 0;
  flex-direction: column;
  overflow: hidden;

  .main-header {
    height: var(--header-height) !important;
    line-height: var(--header-height);
    background: var(--bg-secondary);
    border-bottom: 1px solid var(--border-color);
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0 24px;
    gap: 16px;
    flex-shrink: 0;

    .header-left {
      min-width: 0;
      flex: 1;
      .page-title {
        font-size: 16px;
        font-weight: 600;
        color: var(--text-primary);
        line-height: 1;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
      .breadcrumb {
        margin-top: 4px;
        .el-breadcrumb__item {
          .el-breadcrumb__inner { color: var(--text-muted) !important; font-size: 12px; }
          &:last-child .el-breadcrumb__inner { color: var(--text-secondary) !important; }
        }
      }
    }

    .header-right {
      display: flex;
      align-items: center;
      gap: 16px;
      flex-shrink: 0;

      .header-time {
        font-size: 13px;
        color: var(--text-muted);
        font-variant-numeric: tabular-nums;
        white-space: nowrap;
      }

      .header-divider {
        width: 1px; height: 20px;
        background: var(--border-color);
      }

      .user-info {
        display: flex;
        align-items: center;
        gap: 8px;
        cursor: pointer;
        padding: 6px 10px;
        border-radius: var(--radius);
        transition: background 0.2s;

        &:hover { background: var(--bg-hover); }

        .user-avatar {
          width: 28px; height: 28px;
          border-radius: 50%;
          background: linear-gradient(135deg, var(--accent-blue), #0088cc);
          display: flex; align-items: center; justify-content: center;
          font-size: 12px; font-weight: 700; color: white;
        }

        .user-name { font-size: 14px; color: var(--text-secondary); }
        .caret { color: var(--text-muted); font-size: 12px; }
      }
    }
  }

  .app-main {
    flex: 1;
    min-width: 0;
    overflow: auto;
    padding: 24px;
    background: var(--bg-primary);

    &::-webkit-scrollbar { width: 6px; }
    &::-webkit-scrollbar-track { background: transparent; }
    &::-webkit-scrollbar-thumb { background: var(--border-color); border-radius: 3px; }
  }

  .route-switching-tip {
    position: fixed;
    top: 76px;
    right: 24px;
    z-index: 1800;
    padding: 8px 14px;
    border: 1px solid var(--border-default);
    border-radius: 999px;
    background: var(--bg-elevated);
    color: var(--text-secondary);
    box-shadow: var(--shadow-lg);
    font-size: 13px;
    pointer-events: none;
  }
}

@media (max-width: 1366px) {
  html, body, #app,
  .app-wrapper {
    min-width: 0;
  }

  .sidebar-container {
    width: 220px !important;

    .logo-container {
      padding: 0 14px;
    }

    .sidebar-menu {
      padding: 0 8px;

      .el-menu-item {
        font-size: 13px;
      }
    }

    .menu-section-label,
    .theme-toggle,
    .sidebar-footer {
      padding-left: 14px;
      padding-right: 14px;
    }
  }

  .main-container {
    .main-header {
      padding: 0 18px;
    }

    .app-main {
      padding: 18px;
    }
  }
}

@media (max-width: 1100px) {
  .sidebar-container {
    width: 190px !important;

    .logo-container {
      padding: 0 10px;

      .subtitle { display: none; }
    }

    .sidebar-menu {
      padding: 0 6px;

      .el-menu-item {
        padding-left: 12px !important;
        padding-right: 10px !important;
      }
    }
  }

  .main-container {
    .main-header {
      padding: 0 12px;

      .header-time,
      .header-divider { display: none; }
    }

    .app-main { padding: 12px; }
  }
}

@media (max-height: 760px) {
  .sidebar-container {
    .sidebar-menu .el-menu-item {
      height: 38px;
      line-height: 38px;
    }

    .menu-section-label {
      padding-top: 10px;
    }
  }
}

// ======== 路由过渡动画 ========
.fade-slide-enter-active,
.fade-slide-leave-active {
  transition: all 0.2s ease;
}
.fade-slide-enter-from {
  opacity: 0;
  transform: translateY(8px);
}
.fade-slide-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}

// ======== 全局覆盖 Element Plus 到暗色主题 ========
.el-card {
  background: var(--bg-card) !important;
  border: 1px solid var(--border-color) !important;
  border-radius: var(--radius) !important;
  color: var(--text-primary) !important;
  box-shadow: none !important;

  .el-card__header {
    background: var(--bg-card) !important;
    border-bottom: 1px solid var(--border-color) !important;
    color: var(--text-primary) !important;
  }
}

.el-table {
  background: transparent !important;
  color: var(--text-primary) !important;

  .el-table__header-wrapper th {
    background: var(--bg-secondary) !important;
    color: var(--text-secondary) !important;
    border-bottom: 1px solid var(--border-color) !important;
    font-weight: 500;
    font-size: 12px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }

  .el-table__body tr {
    background: transparent !important;
    &:hover td { background: var(--bg-hover) !important; }
    td {
      background: transparent !important;
      border-bottom: 1px solid var(--border-subtle) !important;
      color: var(--text-primary) !important;
    }
  }

  .el-table__inner-wrapper::before { background: var(--border-color) !important; }
}

.el-button {
  border-radius: 6px !important;
  font-weight: 500 !important;

  &.el-button--primary {
    background: var(--accent-blue) !important;
    border-color: var(--accent-blue) !important;
    color: #000 !important;
    &:hover { opacity: 0.85; }
  }

  &.el-button--success {
    background: var(--accent-green) !important;
    border-color: var(--accent-green) !important;
    color: #000 !important;
  }
}

.el-input .el-input__wrapper {
  background: var(--bg-primary) !important;
  border: 1px solid var(--border-color) !important;
  border-radius: 6px !important;
  box-shadow: none !important;
  &:hover, &.is-focus { border-color: var(--accent-blue) !important; }
  .el-input__inner { color: var(--text-primary) !important; }
}

.el-select .el-input__wrapper {
  background: var(--bg-primary) !important;
  border: 1px solid var(--border-color) !important;
}

.el-select-dropdown {
  background: var(--bg-card) !important;
  border: 1px solid var(--border-color) !important;
}

/* 修复全局下拉框“白底白字/焦点态错色”问题（含销售单列表状态筛选） */
.el-select-dropdown__item {
  color: var(--text-primary) !important;
  background: transparent !important;
}
.el-select-dropdown__item.hover,
.el-select-dropdown__item:hover,
.el-select-dropdown__item.is-hovering {
  background: var(--bg-hover) !important;
  color: var(--text-primary) !important;
}
.el-select-dropdown__item.selected,
.el-select-dropdown__item.is-selected {
  background: var(--color-primary-bg) !important;
  color: var(--color-primary) !important;
}
.el-select-dropdown__item.selected.is-hovering,
.el-select-dropdown__item.is-selected.is-hovering,
.el-select-dropdown__item.selected:hover,
.el-select-dropdown__item.is-selected:hover {
  background: var(--color-primary-bg) !important;
  color: var(--color-primary) !important;
}
.el-select-dropdown__item.is-disabled {
  background: transparent !important;
  color: var(--text-muted) !important;
}

.el-tag {
  border-radius: var(--radius-sm) !important;
  &.el-tag--success { background: var(--color-success-bg) !important; border-color: var(--border-default) !important; color: var(--color-success) !important; }
  &.el-tag--warning { background: var(--color-warning-bg) !important; border-color: var(--border-default) !important; color: var(--color-warning) !important; }
  &.el-tag--danger  { background: var(--color-danger-bg) !important; border-color: var(--border-default) !important; color: var(--color-danger) !important; }
  &.el-tag--info    { background: var(--bg-hover) !important; border-color: var(--border-default) !important; color: var(--text-secondary) !important; }
}

.el-dropdown-menu {
  background: var(--bg-card) !important;
  border: 1px solid var(--border-color) !important;
  .el-dropdown-menu__item { color: var(--text-primary) !important; &:hover { background: var(--bg-hover) !important; } }
}

.el-dialog {
  background: var(--bg-card) !important;
  border: 1px solid var(--border-color) !important;
  .el-dialog__title { color: var(--text-primary) !important; }
  .el-dialog__headerbtn .el-dialog__close { color: var(--text-muted) !important; }
}

.el-message-box {
  background: var(--bg-card) !important;
  border: 1px solid var(--border-color) !important;
  .el-message-box__title { color: var(--text-primary) !important; }
  .el-message-box__content { color: var(--text-secondary) !important; }
}

</style>
